import { Callout } from '@/components/ui/callout'
import { ErrorState } from '@/components/ui/states'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { useGhlSettings } from '@/hooks/useGoHighLevel'
import type { VenueDto } from '@lib/dto/venue.dto'
import { AddGhlConnectionForm } from './AddGhlConnectionForm'
import { GhlConnectionCard } from './GhlConnectionCard'
import { GhlNameFormatSummary } from './GhlNameFormatSummary'
import { GhlBackfillPanel } from './GhlBackfillPanel'
import { GhlReviewList } from './GhlReviewList'
import { useAuth } from '@/auth/AuthContext'
import { isInTenantSupportMode } from '@/auth/roles'

/**
 * The GoHighLevel section of a venue's settings (docs/gohighlevel.spec.md): the
 * sub-accounts that hold its leads, what each feeds the event picker, and the
 * company's event name format. Shown only to an organization admin of a company
 * with the feature.
 *
 * Each card reads its sub-account from GoHighLevel as it is drawn. That is fine
 * here because the settings page mounts only the section being looked at, so
 * GoHighLevel is asked only when someone opens this one.
 */
export function VenueGoHighLevelSection({ venue }: { venue: VenueDto }) {
  const settings = useGhlSettings(venue.id, true)
  const { user } = useAuth()

  if (settings.isPending) return <p className="m-0 text-sm text-ink-muted">Checking connections…</p>
  if (settings.isError) {
    if (isApiError(settings.error) && settings.error.status === 503) {
      return (
        <Callout tone="attention" title="GoHighLevel isn’t available right now">
          Connections can’t be added or used at the moment. Try again later.
        </Callout>
      )
    }
    return <ErrorState message={requestErrorMessage(settings.error)} onRetry={() => void settings.refetch()} />
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <p className="m-0 text-sm text-ink-muted">
          Each connection is one GoHighLevel sub-account. Add every sub-account whose leads can book at this venue.
        </p>
        {settings.data.connections.map((connection) => (
          <GhlConnectionCard key={connection.id} venue={venue} connection={connection} />
        ))}
        <AddGhlConnectionForm venue={venue} />
      </div>

      <div className="space-y-3">
        <h3 className="m-0 text-sm font-medium text-ink">Event names</h3>
        <GhlNameFormatSummary nameFormat={settings.data.nameFormat} />
      </div>

      {settings.data.connections.length > 0 ? (
        <>
          {isInTenantSupportMode(user) ? (
            <div className="space-y-3">
              <h3 className="m-0 text-sm font-medium text-ink">Backfill</h3>
              <GhlBackfillPanel venueId={venue.id} />
            </div>
          ) : null}
          <div className="space-y-3">
            <h3 className="m-0 text-sm font-medium text-ink">Review</h3>
            <GhlReviewList venueId={venue.id} />
          </div>
        </>
      ) : null}
    </div>
  )
}
